import translations from '../../../../../../../../translations';
import type { PageComponentValidationAPI } from '../../../../../../../../types/validation';
import { isFullValueReference, isValueReference } from '../../../../../../../../utils/guard';
import ButtonDefault from '../../../../../../../buttons/ButtonDefault';
import ButtonIcon from '../../../../../../../buttons/ButtonIcon';
import Glyphicon from '../../../../../../../generic/Glyphicon';
import Table from '../../../../../../../generic/Table';
import ValueReferenceDisplay from '../../../../../../../generic/ValueReferenceDisplay';
import Loader from '../../../../../../../loader/Loader';
import type { ValidationAction } from './ValidationConfig';

interface Props {
    validations: PageComponentValidationAPI[];
    updateValidations: (validations: PageComponentValidationAPI[]) => void;
    dispatchValidations: (value: ValidationAction) => void;
    isLoadingValueReferences: boolean;
}

const ValidationList = ({
    validations,
    updateValidations,
    dispatchValidations,
    isLoadingValueReferences,
}: Props) => {
    const removeValidation = (removeIndex: number) => {
        updateValidations(validations.filter((_, index) => index !== removeIndex));
    };

    const onReorder = (orderedRows: PageComponentValidationAPI[]) => {
        updateValidations(orderedRows);
    };

    return (
        <>
            {validations.length > 0 && (
                <p>
                    Validations in the list below will only apply when the component has a value
                    supplied.
                </p>
            )}
            <div className="flex">
                <ButtonDefault onClick={() => dispatchValidations({ type: 'addValidationClick' })}>
                    <Glyphicon glyph="plus" />
                    {translations.PAGE_BUILDER_validation_add}
                </ButtonDefault>
            </div>
            {validations.length > 0 && (
                <Table
                    tableId="validation-list-table"
                    onReorder={onReorder}
                    // Function must be passed for rows to selectable, which is needed for re-ordering
                    onRowSelect={() => undefined}
                    columns={[
                        {
                            renderHeader: () => translations.COMMON_TABLE_type,
                            renderCell: ({ item }) => item.type,
                        },
                        {
                            renderHeader: () => translations.COMMON_TABLE_value,
                            renderCell: ({ item }) => {
                                if (isFullValueReference(item.value)) {
                                    return <ValueReferenceDisplay valueReference={item.value} />;
                                }
                                // Show loading indicator while value reference is being enriched
                                if (isValueReference(item.value)) {
                                    if (isLoadingValueReferences) {
                                        return <Loader small />;
                                    }
                                    // Fallback if enrichment failed
                                    return `${item.value.id ?? 'Value'}`;
                                }
                                // minimum and maximum validation could be a date / datetime or a number
                                if (
                                    (item.type === 'Minimum' || item.type === 'Maximum') &&
                                    typeof item.value === 'string' &&
                                    Number.isNaN(Number(item.value))
                                ) {
                                    try {
                                        const date = new Date(item.value);
                                        if (item.value.toLowerCase().includes('t')) {
                                            // This is a date and looks like a datetime because the value includes a "T"
                                            return date.toLocaleString();
                                        }
                                        return date.toLocaleDateString();
                                    } catch (_e) {
                                        return item.value;
                                    }
                                }
                                return item.value;
                            },
                        },
                        {
                            renderHeader: () => translations.COMMON_TABLE_message,
                            renderCell: ({ item }) => item.message,
                        },
                        {
                            renderHeader: () => translations.COMMON_TABLE_actions,
                            renderCell: ({ rowIndex }) => (
                                <div className="action-btn-wrapper">
                                    <ButtonIcon
                                        glyph="Edit"
                                        aria-label="Edit"
                                        onClick={() => {
                                            dispatchValidations({
                                                type: 'editValidationClick',
                                                payload: {
                                                    selectedValidationIndex: rowIndex,
                                                },
                                            });
                                        }}
                                        iconClass="icon-small"
                                    />
                                    <ButtonIcon
                                        className="danger"
                                        glyph="Delete"
                                        aria-label="Delete"
                                        onClick={() => removeValidation(rowIndex)}
                                        iconClass="icon-small"
                                    />
                                </div>
                            ),
                            size: '8rem',
                        },
                    ]}
                    items={validations}
                />
            )}
        </>
    );
};

export default ValidationList;
